import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, CheckCircle2, Clock, Star, Trash2 } from "lucide-react";
import { myPeerReviewSubmissions, deletePeerReviewSubmission } from "@/lib/apiClient";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Peer Reviewed tab — read-only status view of this module's peer-review
 * submissions, sourced from the existing /peer-reviews/my-submissions API
 * (no new backend endpoint needed).
 */
export default function TestPeerReviewed({ module: m }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const load = () => {
    setLoading(true);
    myPeerReviewSubmissions()
      .then((res) => setSubmissions((res.items || []).filter((s) => s.module_code === m.code)))
      .catch(() => setSubmissions([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [m.code]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (submissionId) => {
    setDeletingId(submissionId);
    try {
      await deletePeerReviewSubmission(submissionId);
      setSubmissions((prev) => prev.filter((s) => s.submission_id !== submissionId));
      toast.success(t("peer_review.toast_submission_deleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("peer_review.toast_delete_failed"));
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6 text-sm text-[#4B5563]" data-testid="peer-reviewed-loading">{t("rd.loading")}</div>;
  }

  if (submissions.length === 0) {
    return (
      <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6" data-testid="peer-reviewed-empty">
        <div className="flex items-start gap-3">
          <Users className="w-6 h-6 text-[#D97706] flex-shrink-0" />
          <div>
            <div className="font-medium text-[#1F2937]">{t("rd.peer_empty_title")}</div>
            <div className="text-sm text-[#4B5563] mt-1">{t("rd.peer_empty_body")}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="peer-reviewed-list">
      {submissions.map((s) => {
        const decisions = s.decisions || [];
        const isReviewed = Boolean(s.peer_reviewed_badge);
        return (
          <div key={s.submission_id} className="rounded-2xl bg-white border border-[#E7E0D2] p-5" data-testid={`peer-submission-${s.submission_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                {s.status === "open" ? <Clock className="w-5 h-5 text-[#D97706]" /> : <CheckCircle2 className="w-5 h-5 text-[#10B981]" />}
                <div>
                  <div className="text-sm font-medium text-[#1F2937]">
                    {s.status === "open" ? t("rd.peer_status_open") : t("rd.peer_status_closed")}
                  </div>
                  <div className="text-xs text-[#4B5563]">{new Date(s.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FDFBF7] border border-[#E7E0D2] text-xs font-medium text-[#1F2937]">
                {t("rd.peer_reviews_count", { n: decisions.length })}
              </span>
              {isReviewed && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] text-xs font-medium text-[#064E3B]">
                  <Star className="w-3 h-3" /> {t("rd.peer_badge_earned")}
                </span>
              )}
              {s.status === "open" && (
                <button
                  data-testid={`peer-submission-delete-${s.submission_id}`}
                  onClick={() => remove(s.submission_id)}
                  disabled={deletingId === s.submission_id}
                  title={t("peer_review.delete_this_submission")}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#E7E0D2] hover:border-[#EF4444] text-xs font-medium text-[#4B5563] hover:text-[#EF4444] transition disabled:opacity-50">
                  <Trash2 className="w-3 h-3" /> {t("peer_review.delete")}
                </button>
              )}
            </div>
            {decisions.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#E7E0D2] flex flex-wrap gap-2" data-testid={`peer-decisions-${s.submission_id}`}>
                {decisions.map((d) => (
                  <span key={d.decision_id} className="text-xs px-2.5 py-1 rounded-full bg-[#FDFBF7] border border-[#E7E0D2] text-[#4B5563]">
                    {t("rd.peer_decision_rubric", { avg: d.rubric ? Object.values(d.rubric).filter((v) => typeof v === "number").reduce((a, b) => a + b, 0) / Math.max(Object.values(d.rubric).filter((v) => typeof v === "number").length, 1) : 0 })}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <Link to="/certificate?tab=peer" className="inline-flex items-center gap-1.5 text-sm text-[#064E3B] hover:underline" data-testid="peer-reviewed-view-all">
        {t("rd.peer_view_all")} →
      </Link>
    </div>
  );
}
